import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import styled from 'styled-components';
import {
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  FormField,
  FormInput,
  getFormTableFieldName,
  TanstackFormTable,
  RequiredText,
  User,
  useUserInfo,
  FormSelect,
  SAMPLE_COMMENT_GROUP_VALUE,
  SampleCommentValidFormDataType,
  GroupDescriptionType,
  formatCodeName,
  formatDateTimeShort,
} from '@atlas-frontend/sdk';
import translations from './SampleCommentsTable.translations.json';
import { useInputDataDetailsStoreValue } from '../details/InputDataDetailsContext';
import { useWatch } from 'react-hook-form';
import { colorTextQuaternary } from '@atlas-frontend/design-tokens';
import { getCommentDescriptionLabel } from './getCommentDescriptionLabel';

const { addTranslations, useTranslation } = i18nNsFactory(
  'sample-comments-edit-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<SampleCommentValidFormDataType>();

export const SampleCommentsEditTable: FC<{
  descriptionId?: number;
  descriptions: GroupDescriptionType[];
  sampleId: number;
  isLoading?: boolean;
  groupId?: number;
}> = ({ descriptionId, descriptions, sampleId, isLoading, groupId }) => {
  const { t } = useTranslation();
  const { user } = useUserInfo();

  const commentCount = useWatch({ name: 'comments.length' });
  const [, setTotal] = useInputDataDetailsStoreValue('commentsTotal');

  useEffect(() => setTotal(commentCount ?? 0), [commentCount, setTotal]);

  const groupOrInputOptions = useMemo(
    () => [
      {
        value: SAMPLE_COMMENT_GROUP_VALUE,
        label: t('groupOrInputOptions.group'),
        disabled: false,
      },
      ...descriptions
        .filter((description) => description.id !== null)
        .map((description) => ({
          label: formatCodeName(description),
          value: description.id as number,
          disabled: description.activeVersionId === null,
        })),
    ],
    [descriptions, t],
  );

  const getRowOptions = useCallback(
    (comment: SampleCommentValidFormDataType) => {
      const rowValue = comment.sampleDataObjectDescriptionId;
      const isMissingDescription =
        typeof rowValue === 'number' &&
        !descriptions.some((description) => description.id === rowValue);

      if (!isMissingDescription) {
        return groupOrInputOptions;
      }

      return [
        ...groupOrInputOptions,
        {
          label: getCommentDescriptionLabel(comment),
          value: rowValue,
          disabled: true,
        },
      ];
    },
    [descriptions, groupOrInputOptions],
  );

  const defaultRowValues = useMemo(
    () => ({
      sampleDataObjectDescriptionId:
        groupId !== undefined
          ? (groupOrInputOptions.find(
              (option) => option.value === descriptionId && !option.disabled,
            )?.value ?? SAMPLE_COMMENT_GROUP_VALUE)
          : null,
      sampleId,
      comments: '',
    }),
    [descriptionId, groupId, groupOrInputOptions, sampleId],
  );

  const cols = useMemo(
    () =>
      [
        groupId !== undefined &&
          columnHelper.accessor('sampleDataObjectDescriptionId', {
            header: t('column.groupOrInput'),
            cell: (props) => {
              const name = getFormTableFieldName(props);
              const options = getRowOptions(props.row.original);
              return (
                <FormField
                  name={name}
                  size="small"
                  preview={{
                    previewOnly: !isEditable(props, user),
                    render: (val) =>
                      options.find((option) =>
                        val
                          ? option.value === val
                          : option.value === SAMPLE_COMMENT_GROUP_VALUE,
                      )?.label,
                  }}
                >
                  <FormSelect
                    name={name}
                    size="small"
                    autoFocus
                    defaultOpen
                    options={options}
                  />
                </FormField>
              );
            },
            filterFn: 'includesOption',
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              minWidth: 150,
            },
          }),
        columnHelper.accessor('createdBy.username', {
          header: t('column.user'),
          meta: {
            width: '20ch',
          },
        }),
        columnHelper.accessor('comments', {
          header: () => <RequiredText>{t('column.comment')}</RequiredText>,
          cell: (props) => {
            const name = getFormTableFieldName(props);

            return (
              <FormField
                name={name}
                size="small"
                preview={{
                  previewOnly: !isEditable(props, user),
                }}
              >
                <FormInput
                  autoFocus
                  maxLength={2000}
                  name={name}
                  rows={3}
                  size="small"
                />
              </FormField>
            );
          },
          meta: {
            minWidth: 150,
          },
          enableSorting: false,
        }),
        columnHelper.accessor('createdAt', {
          header: t('column.createdAt'),
          cell: (props) => formatDateTimeShort(props.renderValue()) ?? '',
          filterFn: 'formattedDate',
          meta: {
            width: 200,
          },
        }),
        columnHelper.accessor('modifiedAt', {
          header: t('column.modifiedAt'),
          cell: (props) => formatDateTimeShort(props.renderValue()) ?? '',
          filterFn: 'formattedDate',
          meta: {
            width: 200,
          },
        }),
        columnHelper.accessor('operations' as any, {
          header: () => (
            <Flex justify="center" gap="small">
              <CreateFormTableRowButton
                title={t('operations.create')}
                autoFocusField="comments"
                defaultRowValues={defaultRowValues}
              />
            </Flex>
          ),
          enableSorting: false,
          enableColumnFilter: false,
          cell: (props) =>
            isEditable(props, user) && (
              <Flex justify="center" gap="small">
                <DeleteFormTableRowButton id="delete-comment" context={props} />
              </Flex>
            ),
          meta: {
            width: 30,
            className: 'sticky-right',
          },
        }),
      ].filter((column) => !!column) as ColumnDef<
        SampleCommentValidFormDataType,
        any
      >[],
    [defaultRowValues, getRowOptions, groupId, t, user],
  );

  return (
    <TableWrapper>
      <TanstackFormTable
        name="comments"
        columns={cols}
        defaultSort={[{ id: 'createdAt', desc: true }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
        isLoading={isLoading}
        rowHtmlAttributes={(comment) => ({
          className: isActiveDescription(comment, descriptions)
            ? ''
            : 'inactive',
        })}
      />
    </TableWrapper>
  );
};

const isEditable = (
  props: CellContext<SampleCommentValidFormDataType, any>,
  user: User,
) => !props.row.original.id || props.row.original.createdBy?.id === user?.id;

const isActiveDescription = (
  comment: SampleCommentValidFormDataType,
  descriptions: GroupDescriptionType[],
) => {
  if (comment.id === undefined || comment.id === null) {
    return true;
  }

  const description = descriptions.find(
    (description) =>
      description.id === comment.sampleDataObjectDescriptionId ||
      comment.sampleDataObjectDescriptionId === SAMPLE_COMMENT_GROUP_VALUE,
  );
  if (!description) return false;

  return description.activeVersionId !== null;
};

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .inactive * {
    color: ${colorTextQuaternary};
  }
`;
